import { ItemsPerPage } from '@billy/design-system'
import { useCallback } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'

import { BillStatus } from '@features/bills/enums/billStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useBulkNavigationQueryParams } from '../../../hooks/useBulkNavigationQueryParams'
import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { BillsSortProperty } from '../query-api'
import { useBillsListFilters } from '../routes/BillsList/legacy'
import { useFetchBills } from '../routes/BillsList/legacy/hooks/useFetchBills'
import { BillUrlParams } from '../types/billUrlParams'
import { useBulkNavigation } from './useBulkNavigation'

export const useBulkBillsNavigation = () => {
  const { bulkBillId } = useParams<BillUrlParams>()
  const isEnabled = !!bulkBillId
  const [billsListQueryParams] = useBillsListFilters({ enabled: isEnabled })
  const [bulkBillsQueryParams, setBulkBillsQueryParams] = useBulkNavigationQueryParams({
    enabled: isEnabled,
    initialQuery: {
      ...(billsListQueryParams.page ? { bulkPage: billsListQueryParams.page } : {}),
      ...(billsListQueryParams.pageSize ? { bulkPageSize: billsListQueryParams.pageSize } : {}),
      ...(billsListQueryParams.sortDirection ? { bulkSortDirection: billsListQueryParams.sortDirection } : {}),
      ...(billsListQueryParams.sortProperty ? { bulkSortProperty: billsListQueryParams.sortProperty } : {}),
    },
  })

  const navigate = useNavigate()
  const location = useLocation()
  const { organization } = useUserOrganization()

  const { bulkPage, bulkPageSize, bulkSortDirection, bulkSortProperty } = bulkBillsQueryParams
  const page = Number(bulkPage)
  const pageSize = Number(bulkPageSize) as ItemsPerPage

  const handleChangePage = useCallback(
    (bulkBillId: string | undefined) => {
      navigate(
        `${getOrganizationViewUrl(organization?.url, reactRoute.organization.bills.getEditBulkRoute(bulkBillId || ''))}${
          location.search
        }`,
      )
    },
    [navigate, location.search, organization?.url],
  )

  const { bills, isLoading, pagination } = useFetchBills({
    isDisabled: !isEnabled,
    page,
    pageSize,
    sortDirection: bulkSortDirection,
    sortProperty: bulkSortProperty as BillsSortProperty,
    status: BillStatus.Draft,
  })

  const { itemsToGo, next, previous, hasNext, hasPrevious } = useBulkNavigation({
    currentId: bulkBillId,
    isLoading,
    items: bills || [],
    page,
    onPageChange: handleChangePage,
    onQueryParamsChange: setBulkBillsQueryParams,
    pageSize,
    pagination,
  })

  return {
    billsToGo: itemsToGo,
    goToNextBill: next,
    goToPreviousBill: previous,
    hasNextBill: hasNext,
    hasPreviousBill: hasPrevious,
    isLoading,
  }
}
